import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import {
  getRevenueLeakSummary,
  type LeakCategory,
  type UrgencyLevel,
} from "@/data/revenueLeakSelectors";
import {
  buildRecoveryActions,
  getRecoveryDef,
  logRecoveryAction,
  isDuplicateRecovery,
  makeDedupeKey,
  type RecoveryAction,
  type RecoveryStatus,
} from "@/data/recoveryActionEngine";
import type { FollowUpTask } from "@/data/opportunityTypes";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useUISkin } from "@/data/UISkinContext";
import { cn } from "@/lib/utils";
import {
  ShieldAlert,
  ArrowRight,
  PhoneCall,
  CalendarPlus,
  RefreshCw,
  Send,
  ClipboardCheck,
  ChevronDown,
  ChevronUp,
  Zap,
  Check,
  Loader2,
  X,
} from "lucide-react";

const URGENCY_STYLES: Record<UrgencyLevel, { badge: "destructive" | "secondary" | "outline"; dot: string }> = {
  high: { badge: "destructive", dot: "bg-destructive" },
  medium: { badge: "outline", dot: "bg-warning/60" },
  low: { badge: "secondary", dot: "bg-muted-foreground" },
};

const ACTION_ICONS: Record<string, React.ElementType> = {
  PhoneCallback: PhoneCall,
  CalendarPlus,
  ShieldAlert,
  RefreshCw,
  Send,
  ClipboardCheck,
};

const STATUS_CONFIG: Record<RecoveryStatus, { label: string; icon: React.ElementType; className: string }> = {
  pending: { label: "Pending", icon: Zap, className: "text-muted-foreground" },
  in_progress: { label: "In Progress", icon: Loader2, className: "text-primary" },
  completed: { label: "Recovered", icon: Check, className: "text-success" },
  ignored: { label: "Ignored", icon: X, className: "text-muted-foreground/60" },
};

const fmt = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n.toFixed(0)}`;

export function RevenueLeakPanel() {
  const {
    appointmentResults, salesOpportunities, followUpTasks,
    conversations, messages, setFollowUpTasks, jobs,
  } = useAppData();
  const navigate = useNavigate();
  const { skin } = useUISkin();
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  // Status map: dedupeKey → RecoveryStatus
  const [statusMap, setStatusMap] = useState<Map<string, RecoveryStatus>>(new Map());

  const summary = useMemo(
    () =>
      getRevenueLeakSummary({
        appointmentResults,
        salesOpportunities,
        followUpTasks,
        conversations,
        messages,
        jobs,
      }),
    [appointmentResults, salesOpportunities, followUpTasks, conversations, messages, jobs],
  );

  const recoveryActions = useMemo(
    () => buildRecoveryActions(summary.categories),
    [summary.categories],
  );

  const activeCategories = summary.categories.filter((c) => c.count > 0);

  const updateStatus = useCallback((recordId: string, actionType: string, newStatus: RecoveryStatus, oldStatus?: RecoveryStatus) => {
    const key = makeDedupeKey(recordId, actionType as any);
    setStatusMap((prev) => {
      const next = new Map(prev);
      next.set(key, newStatus);
      return next;
    });
    logRecoveryAction({
      sourceLeakType: "no_sale_no_followup", // will be overridden at call site
      recordId,
      actionType: "status_transition",
      actor: "user",
      details: `Status: ${oldStatus || "none"} → ${newStatus}`,
      fromStatus: oldStatus,
      toStatus: newStatus,
    });
  }, []);

  const getActionStatus = useCallback((action: RecoveryAction): RecoveryStatus => {
    const key = makeDedupeKey(action.recordId, action.action.actionType);
    return statusMap.get(key) || "pending";
  }, [statusMap]);

  const executeRecoveryAction = useCallback(
    (action: RecoveryAction) => {
      const actionType = action.action.actionType;

      // Deduplication guard
      if (isDuplicateRecovery(action.recordId, actionType, statusMap)) {
        toast.warning("Recovery action already in progress or completed for this record");
        logRecoveryAction({
          sourceLeakType: action.leakType,
          recordId: action.recordId,
          actionType: "duplicate_prevented",
          actor: "user",
          details: `Duplicate ${actionType} blocked for ${action.recordId}`,
        });
        return;
      }

      const dedupeKey = makeDedupeKey(action.recordId, actionType);

      if (actionType === "create_followup" || actionType === "escalate_manager") {
        const isEscalation = actionType === "escalate_manager";
        const opp = salesOpportunities.find((o) => o.id === action.recordId);
        const newTask: FollowUpTask = {
          id: `fu-recovery-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          opportunityId: action.recordId,
          type: "rep",
          assignedTo: opp?.assignedRep || "Unassigned",
          dueDate: new Date().toISOString(),
          status: "pending",
          notes: isEscalation
            ? `[ESCALATED] High-value deal recovery — $${action.revenueAtRisk.toLocaleString()} at risk`
            : `Recovery follow-up created from Revenue Leak detection`,
          createdAt: new Date().toISOString(),
          disposition: {} as any,
          customerName: action.recordLabel,
          customerPhone: opp?.primaryPhone || "",
          priority: isEscalation ? "high" : "medium",
        };
        setFollowUpTasks((prev) => [...prev, newTask]);
        logRecoveryAction({
          sourceLeakType: action.leakType,
          recordId: action.recordId,
          actionType,
          actor: "user",
          details: `Created ${isEscalation ? "escalated " : ""}follow-up task ${newTask.id}`,
          toStatus: "in_progress",
        });
        toast.success(isEscalation ? "Manager escalation created" : "Recovery follow-up created");
      } else if (actionType === "create_reschedule") {
        const opp = salesOpportunities.find((o) => o.id === action.recordId);
        if (opp) {
          const newTask: FollowUpTask = {
            id: `fu-resched-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            opportunityId: action.recordId,
            type: "rep",
            assignedTo: opp.assignedRep || "Unassigned",
            dueDate: new Date().toISOString(),
            status: "pending",
            notes: "Reschedule task — no demo was completed",
            createdAt: new Date().toISOString(),
            disposition: {} as any,
            customerName: action.recordLabel,
            customerPhone: opp.primaryPhone || "",
            priority: "high",
          };
          setFollowUpTasks((prev) => [...prev, newTask]);
          logRecoveryAction({
            sourceLeakType: action.leakType,
            recordId: action.recordId,
            actionType,
            actor: "user",
            details: `Created reschedule task ${newTask.id}`,
            toStatus: "in_progress",
          });
          toast.success("Reschedule task created");
        }
      } else if (actionType === "reactivate_followup") {
        setFollowUpTasks((prev) =>
          prev.map((t) =>
            t.opportunityId === action.recordId && t.status !== "completed"
              ? { ...t, priority: "high", dueDate: new Date().toISOString(), status: "pending" as const }
              : t,
          ),
        );
        logRecoveryAction({
          sourceLeakType: action.leakType,
          recordId: action.recordId,
          actionType,
          actor: "user",
          details: "Reactivated and bumped priority to high",
          toStatus: "in_progress",
        });
        toast.success("Follow-up reactivated with high priority");
      } else if (actionType === "start_outreach") {
        const newTask: FollowUpTask = {
          id: `fu-outreach-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          opportunityId: action.recordId,
          type: "rep",
          assignedTo: salesOpportunities.find((o) => o.id === action.recordId)?.assignedRep || "Unassigned",
          dueDate: new Date().toISOString(),
          status: "pending",
          notes: "Initial outreach — uncontacted lead recovery",
          createdAt: new Date().toISOString(),
          disposition: {} as any,
          customerName: action.recordLabel,
          customerPhone: salesOpportunities.find((o) => o.id === action.recordId)?.primaryPhone || "",
          priority: "high",
        };
        setFollowUpTasks((prev) => [...prev, newTask]);
        logRecoveryAction({
          sourceLeakType: action.leakType,
          recordId: action.recordId,
          actionType,
          actor: "user",
          details: `Created outreach task ${newTask.id}`,
          toStatus: "in_progress",
        });
        toast.success("Outreach task created");
      } else if (actionType === "log_result") {
        logRecoveryAction({
          sourceLeakType: action.leakType,
          recordId: action.recordId,
          actionType,
          actor: "user",
          details: "Navigated to log appointment result",
          toStatus: "in_progress",
        });
        setStatusMap((prev) => {
          const next = new Map(prev);
          next.set(dedupeKey, "in_progress");
          return next;
        });
        navigate("/appointment-results");
        return;
      }

      // Mark as in_progress
      setStatusMap((prev) => {
        const next = new Map(prev);
        next.set(dedupeKey, "in_progress");
        return next;
      });
    },
    [salesOpportunities, setFollowUpTasks, navigate, statusMap],
  );

  const dismissAction = useCallback((action: RecoveryAction) => {
    const key = makeDedupeKey(action.recordId, action.action.actionType);
    const oldStatus = statusMap.get(key) || "pending";
    setStatusMap((prev) => {
      const next = new Map(prev);
      next.set(key, "ignored");
      return next;
    });
    logRecoveryAction({
      sourceLeakType: action.leakType,
      recordId: action.recordId,
      actionType: "status_transition",
      actor: "user",
      details: `Dismissed: ${oldStatus} → ignored`,
      fromStatus: oldStatus,
      toStatus: "ignored",
    });
    toast("Recovery action dismissed");
  }, [statusMap]);

  const getCategoryActions = useCallback(
    (cat: LeakCategory) => recoveryActions.filter((a) => a.leakType === cat.type),
    [recoveryActions],
  );

  const getCategoryPendingCount = useCallback(
    (cat: LeakCategory) => {
      return getCategoryActions(cat).filter((a) => {
        const s = getActionStatus(a);
        return s === "pending";
      }).length;
    },
    [getCategoryActions, getActionStatus],
  );

  const executeAllForCategory = useCallback(
    (cat: LeakCategory) => {
      const actions = getCategoryActions(cat);
      const autoActions = actions.filter((a) => {
        const s = getActionStatus(a);
        return s === "pending" && a.action.canAutoCreate && !a.action.requiresConfirmation;
      });
      let created = 0;
      for (const action of autoActions) {
        if (!isDuplicateRecovery(action.recordId, action.action.actionType, statusMap)) {
          executeRecoveryAction(action);
          created++;
        }
      }
      if (created > 0) {
        toast.success(`${created} recovery action${created > 1 ? "s" : ""} created`);
      } else if (autoActions.length === 0) {
        toast.info("No pending auto-recoverable actions in this category");
      }
    },
    [getCategoryActions, getActionStatus, executeRecoveryAction, statusMap],
  );

  // Count stats
  const pendingCount = recoveryActions.filter((a) => getActionStatus(a) === "pending").length;
  const inProgressCount = recoveryActions.filter((a) => getActionStatus(a) === "in_progress").length;
  const completedCount = recoveryActions.filter((a) => getActionStatus(a) === "completed").length;

  /* ── Cockpit-skin render: hairline list, type-only header, no card chrome.
        Same data, same handlers, same expand/collapse + recover behavior. ── */
  if (skin === "cockpit") {
    return (
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h2 className="text-[17px] font-semibold tracking-[-0.014em] text-foreground">
              Revenue at Risk
            </h2>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              {fmt(summary.totalRevenueAtRisk)} across {summary.totalLeakCount} Leak{summary.totalLeakCount === 1 ? "" : "s"}
              {pendingCount > 0 ? ` · ${pendingCount} Pending` : ""}
              {inProgressCount > 0 ? ` · ${inProgressCount} In Progress` : ""}
              {completedCount > 0 ? ` · ${completedCount} Recovered` : ""}
            </p>
          </div>
        </div>
        <div className="rounded-[10px] border border-border bg-card overflow-hidden">
          {activeCategories.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-[13px] text-muted-foreground">No active revenue leaks detected.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {activeCategories.map((cat) => {
                const isExpanded = expandedCategory === cat.type;
                const catActions = getCategoryActions(cat);
                const catPending = getCategoryPendingCount(cat);
                const def = getRecoveryDef(cat.type);
                const dotClass = URGENCY_STYLES[cat.urgency].dot;
                return (
                  <li key={cat.type}>
                    <button
                      type="button"
                      onClick={() => setExpandedCategory(isExpanded ? null : cat.type)}
                      className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-muted/30 transition-colors text-left"
                    >
                      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", dotClass)} aria-hidden />
                      <span className="flex-1 min-w-0 text-[14px] text-foreground truncate">
                        {cat.label}
                      </span>
                      {cat.revenueAtRisk > 0 && (
                        <span className="text-[12px] text-muted-foreground tabular-nums shrink-0">
                          {fmt(cat.revenueAtRisk)}
                        </span>
                      )}
                      <span className="text-[12px] text-muted-foreground tabular-nums shrink-0 w-8 text-right">
                        {cat.count}
                      </span>
                      {catPending > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2.5 text-[12px] text-foreground hover:text-foreground"
                          onClick={(e) => { e.stopPropagation(); executeAllForCategory(cat); }}
                          title={def.primary.label}
                        >
                          Recover {catPending}
                        </Button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(cat.navigateTo); }}
                        className="text-[12px] text-muted-foreground hover:text-foreground shrink-0"
                      >
                        Open
                      </button>
                    </button>
                    {isExpanded && catActions.length > 0 && (
                      <ul className="bg-muted/10 border-t border-border divide-y divide-border">
                        {catActions.slice(0, 8).map((action) => {
                          const status = getActionStatus(action);
                          const statusCfg = STATUS_CONFIG[status];
                          const isActionable = status === "pending";
                          return (
                            <li key={action.id} className="flex items-center gap-3 px-5 py-2.5 pl-9">
                              <span className={cn(
                                "flex-1 min-w-0 text-[13px] truncate",
                                status === "ignored" ? "text-muted-foreground line-through" : "text-foreground",
                              )}>
                                {action.recordLabel}
                              </span>
                              {action.revenueAtRisk > 0 && (
                                <span className="text-[12px] text-muted-foreground tabular-nums shrink-0">
                                  {fmt(action.revenueAtRisk)}
                                </span>
                              )}
                              {isActionable ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2.5 text-[12px] text-foreground hover:text-foreground"
                                    onClick={() => executeRecoveryAction(action)}
                                  >
                                    {action.action.label}
                                  </Button>
                                  <button
                                    type="button"
                                    onClick={() => dismissAction(action)}
                                    className="text-[12px] text-muted-foreground/70 hover:text-foreground shrink-0"
                                    title="Dismiss"
                                  >
                                    Dismiss
                                  </button>
                                </>
                              ) : (
                                <span className={cn("text-[12px] shrink-0", statusCfg.className)}>
                                  {statusCfg.label}
                                </span>
                              )}
                            </li>
                          );
                        })}
                        {catActions.length > 8 && (
                          <li className="px-5 py-2 pl-9 text-[12px] text-muted-foreground">
                            +{catActions.length - 8} more — open the full view
                          </li>
                        )}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    );
  }

  /* ── Classic-skin render — original card layout. Untouched. ── */
  return (
    <Card className={summary.overallUrgency === "high" ? "border-destructive/40" : ""}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-destructive" />
          Revenue at Risk
          {summary.totalLeakCount > 0 && (
            <Badge variant="destructive" className="ml-auto text-xs">
              {fmt(summary.totalRevenueAtRisk)}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {/* Summary strip */}
        <div className="flex items-center gap-4 mb-3 px-3 py-2 rounded-md bg-muted/40">
          <div className="text-center">
            <p className="text-lg font-bold text-destructive">{fmt(summary.totalRevenueAtRisk)}</p>
            <p className="text-[10px] text-muted-foreground">Total at Risk</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-foreground">{summary.totalLeakCount}</p>
            <p className="text-[10px] text-muted-foreground">Open Leaks</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-foreground">{pendingCount}</p>
            <p className="text-[10px] text-muted-foreground">Pending</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-primary">{inProgressCount}</p>
            <p className="text-[10px] text-muted-foreground">In Progress</p>
          </div>
          {completedCount > 0 && (
            <div className="text-center">
              <p className="text-lg font-bold text-foreground">{completedCount}</p>
              <p className="text-[10px] text-muted-foreground">Recovered</p>
            </div>
          )}
        </div>

        {/* Leak rows */}
        {activeCategories.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-3">No active revenue leaks detected</p>
        )}
        {activeCategories.map((cat) => {
          const isExpanded = expandedCategory === cat.type;
          const catActions = getCategoryActions(cat);
          const catPending = getCategoryPendingCount(cat);
          const def = getRecoveryDef(cat.type);
          const PrimaryIcon = ACTION_ICONS[def.primary.icon] || Zap;

          return (
            <div key={cat.type}>
              <LeakRow
                category={cat}
                actionLabel={def.primary.label}
                ActionIcon={PrimaryIcon}
                isExpanded={isExpanded}
                pendingCount={catPending}
                onToggle={() => setExpandedCategory(isExpanded ? null : cat.type)}
                onNavigate={() => navigate(cat.navigateTo)}
                onRecoverAll={() => executeAllForCategory(cat)}
              />
              {isExpanded && catActions.length > 0 && (
                <div className="ml-4 mt-1 mb-2 space-y-1 border-l-2 border-muted pl-3">
                  {catActions.slice(0, 8).map((action) => (
                    <RecoveryRow
                      key={action.id}
                      action={action}
                      status={getActionStatus(action)}
                      onExecute={() => executeRecoveryAction(action)}
                      onDismiss={() => dismissAction(action)}
                    />
                  ))}
                  {catActions.length > 8 && (
                    <p className="text-[10px] text-muted-foreground pl-2">
                      +{catActions.length - 8} more — navigate to view all
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function LeakRow({
  category,
  actionLabel,
  ActionIcon,
  isExpanded,
  pendingCount,
  onToggle,
  onNavigate,
  onRecoverAll,
}: {
  category: LeakCategory;
  actionLabel: string;
  ActionIcon: React.ElementType;
  isExpanded: boolean;
  pendingCount: number;
  onToggle: () => void;
  onNavigate: () => void;
  onRecoverAll: () => void;
}) {
  const styles = URGENCY_STYLES[category.urgency];
  const Chevron = isExpanded ? ChevronUp : ChevronDown;

  return (
    <div className="flex items-center gap-1 w-full px-2 py-2 rounded-md text-sm hover:bg-muted/50 transition-colors group">
      <button onClick={onToggle} className="flex items-center gap-2 flex-1 min-w-0 text-left">
        <span className={`h-2 w-2 rounded-full flex-shrink-0 ${styles.dot}`} />
        <span className="text-foreground truncate">{category.label}</span>
        <Chevron className="h-3 w-3 text-muted-foreground flex-shrink-0" />
      </button>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {category.revenueAtRisk > 0 && (
          <span className="text-xs text-muted-foreground">{fmt(category.revenueAtRisk)}</span>
        )}
        <Badge variant={styles.badge} className="text-xs min-w-[24px] justify-center">
          {category.count}
        </Badge>
        {pendingCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-[10px] gap-1 text-primary hover:text-primary"
            onClick={(e) => { e.stopPropagation(); onRecoverAll(); }}
            title={actionLabel}
          >
            <ActionIcon className="h-3 w-3" />
            Recover ({pendingCount})
          </Button>
        )}
        <button onClick={onNavigate} className="p-0.5">
          <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      </div>
    </div>
  );
}

function RecoveryRow({
  action,
  status,
  onExecute,
  onDismiss,
}: {
  action: RecoveryAction;
  status: RecoveryStatus;
  onExecute: () => void;
  onDismiss: () => void;
}) {
  const Icon = ACTION_ICONS[action.action.icon] || Zap;
  const statusCfg = STATUS_CONFIG[status];
  const StatusIcon = statusCfg.icon;
  const isActionable = status === "pending";

  return (
    <div className="flex items-center justify-between py-1 text-xs">
      <div className="flex items-center gap-1.5 min-w-0">
        <Icon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        <span className={`truncate ${status === "ignored" ? "text-muted-foreground line-through" : "text-foreground"}`}>
          {action.recordLabel}
        </span>
        {action.revenueAtRisk > 0 && (
          <span className="text-muted-foreground flex-shrink-0">{fmt(action.revenueAtRisk)}</span>
        )}
      </div>
      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
        {isActionable ? (
          <>
            <Button
              size="sm"
              variant="outline"
              className="h-5 px-2 text-[10px]"
              onClick={onExecute}
            >
              {action.action.label}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
              onClick={onDismiss}
              title="Dismiss"
            >
              <X className="h-3 w-3" />
            </Button>
          </>
        ) : (
          <span className={`flex items-center gap-1 text-[10px] ${statusCfg.className}`}>
            <StatusIcon className="h-3 w-3" />
            {statusCfg.label}
          </span>
        )}
      </div>
    </div>
  );
}
